import React, { useState, useEffect, useRef } from 'react';
import { MapPin, Loader2 } from 'lucide-react';
import { Input } from './ui/input';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Google Places autocomplete input.
 * Mirrors the mobile app's autocomplete UX:
 *  - Debounced (450ms) network calls via backend proxy
 *  - Dropdown of suggestions with main + secondary text
 *  - Selecting a suggestion fetches place details and emits the formatted address
 *
 * Props:
 *  - id: form field id
 *  - value: current input value
 *  - onChange: (newValue) => void  -- raw text changes
 *  - onSelect: ({ address, latitude, longitude }) => void -- after a suggestion is picked
 *  - placeholder
 *  - country: 2-letter ISO code for region biasing (default "ma" for Morocco)
 *  - hasError: bool to apply error styling
 *  - testIdPrefix: for data-testid attribution
 */
const AddressAutocomplete = ({
  id,
  value,
  onChange,
  onSelect,
  placeholder,
  country = 'ma',
  hasError = false,
  testIdPrefix = 'address',
}) => {
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const containerRef = useRef(null);
  const debounceTimer = useRef(null);
  const lastSelectedValue = useRef('');

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchSuggestions = (input) => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }
    if (!input || input.trim().length < 3) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    debounceTimer.current = setTimeout(async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${API}/maps/places/autocomplete`, {
          params: { input, country },
        });
        const preds = response.data?.predictions || [];
        setSuggestions(preds);
        setShowSuggestions(preds.length > 0);
        setActiveIndex(-1);
      } catch {
        setSuggestions([]);
        setShowSuggestions(false);
      } finally {
        setLoading(false);
      }
    }, 450);
  };

  const handleChange = (e) => {
    const newValue = e.target.value;
    onChange(newValue);
    // Don't re-trigger autocomplete if user just selected this exact value
    if (newValue !== lastSelectedValue.current) {
      fetchSuggestions(newValue);
    }
  };

  const handleSelect = async (prediction) => {
    setShowSuggestions(false);
    setSuggestions([]);
    try {
      const response = await axios.get(`${API}/maps/places/details`, {
        params: { place_id: prediction.place_id },
      });
      const details = response.data;
      lastSelectedValue.current = details.address;
      onSelect({
        address: details.address,
        latitude: details.latitude,
        longitude: details.longitude,
      });
    } catch {
      // Fall back to the prediction's full description text
      lastSelectedValue.current = prediction.description;
      onSelect({ address: prediction.description, latitude: null, longitude: null });
    }
  };

  const handleKeyDown = (e) => {
    if (!showSuggestions || suggestions.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => (i + 1) % suggestions.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => (i - 1 + suggestions.length) % suggestions.length);
    } else if (e.key === 'Enter' && activeIndex >= 0) {
      e.preventDefault();
      handleSelect(suggestions[activeIndex]);
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
    }
  };

  return (
    <div ref={containerRef} className="relative">
      <div className="relative">
        <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-amber-500 pointer-events-none" />
        <Input
          id={id}
          type="text"
          value={value}
          onChange={handleChange}
          onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          autoComplete="off"
          className={`bg-black/30 border-white/10 text-white placeholder:text-gray-500 h-12 rounded-xl focus:border-amber-500 focus:ring-amber-500/20 pl-12 pr-10 ${
            hasError ? 'border-red-500' : ''
          }`}
          data-testid={`${testIdPrefix}-input`}
        />
        {loading && (
          <Loader2 className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-amber-500 animate-spin" />
        )}
      </div>

      {showSuggestions && suggestions.length > 0 && (
        <ul
          className="absolute top-full left-0 right-0 mt-2 bg-[#1a1a1a] border border-white/10 rounded-xl overflow-hidden z-50 shadow-2xl max-h-72 overflow-y-auto"
          data-testid={`${testIdPrefix}-suggestions`}
        >
          {suggestions.map((s, idx) => (
            <li
              key={s.place_id}
              onMouseDown={(e) => {
                e.preventDefault(); // keep input focus
                handleSelect(s);
              }}
              onMouseEnter={() => setActiveIndex(idx)}
              className={`px-4 py-3 cursor-pointer transition-colors flex items-start gap-3 ${
                idx === activeIndex ? 'bg-amber-500/15' : 'hover:bg-white/5'
              }`}
              data-testid={`${testIdPrefix}-suggestion-${idx}`}
            >
              <MapPin className="w-4 h-4 text-amber-500 flex-shrink-0 mt-1" />
              <div className="min-w-0">
                <div className="text-white text-sm font-medium truncate">{s.main_text}</div>
                {s.secondary_text && (
                  <div className="text-gray-400 text-xs truncate">{s.secondary_text}</div>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default AddressAutocomplete;
